import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import type { ShopifySectionWidgetProps } from "../types";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { CARD } from "@/config/design-tokens";
import { SectionHeader } from "../../../SectionHeader";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { useDetailEditMode } from "../../../../hooks/useDetailEditMode";
import { useInlineFieldSave } from "@/components/inline-edit/detail/useInlineFieldSave";
import { useBufferFieldSave } from "@/components/inline-edit/detail/useBufferFieldSave";
import { useInlineFieldUndo } from "@/components/inline-edit/detail/useInlineFieldUndo";
import { getAdjacentField } from "@/components/inline-edit/detail/getAdjacentField";
import { InlineFieldRow } from "../../../InlineFieldRow";
import { itemQueryKeys } from "@/features/inventory/api/itemQueryKeys";

/**
 * Fields owned by AdvancedSection — self-sourcing pattern.
 */
const ADVANCED_FIELDS = [
  "country_of_origin",
  "disabled",
  "last_purchase_rate",
  "valuation_rate",
  "end_of_life",
] as const;

const FALLBACK_LABELS: Record<string, string> = {
  country_of_origin: "Country of Origin",
  disabled: "Disabled",
  last_purchase_rate: "Last Purchase Rate",
  valuation_rate: "Valuation Rate",
  end_of_life: "End of Life",
};

/**
 * AdvancedSection — collapsible card for rarely-used fields.
 *
 * Supports dual-mode rendering:
 * - Modal mode: click expanded content → MetaFieldEditModal
 * - Inline mode: click field value → edit in-place
 */
export function AdvancedSection({
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  isSaving,
  metaFields,
  fieldErrors,
  onClearFieldError,
}: ShopifySectionWidgetProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const { textSize, rowPx, rowPy, rowGap, dividerMx, rowLabel } = useDetailDensity();

  // Edit mode: mobile forces modal (built into hook)
  const { editMode } = useDetailEditMode();
  const isNew = !formData?.name;

  // Inline save: network patch for existing docs, local buffer for new docs
  const inlineSave = useInlineFieldSave({
    doctype: "Item",
    docname: formData.name ?? "",
    detailKey: itemQueryKeys.detail(formData.name ?? ""),
  });
  const bufferSave = useBufferFieldSave();
  const { saveField, getFieldState, retryField, undoField, getUndoable, lastSavedField } = isNew ? bufferSave : inlineSave;

  useInlineFieldUndo({ undoField, getUndoable, lastSavedField });

  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  // Cancel active edit when section collapses
  useEffect(() => {
    if (!isExpanded) setEditingField(null);
  }, [isExpanded]);

  const fieldsByName = useMemo(
    () => new Map(metaFields?.map((f) => [f.fieldname, f]) ?? []),
    [metaFields]
  );

  const fieldSchemas = useMemo(() => {
    return ADVANCED_FIELDS.map((fieldname) => {
      const metaField = fieldsByName.get(fieldname);
      if (metaField) return metaField;
      return {
        fieldname,
        label: FALLBACK_LABELS[fieldname] ?? fieldname,
        fieldtype: fieldname === "disabled" ? "Check" : "Data",
      } as DocFieldSchema;
    });
  }, [fieldsByName]);

  const sectionTitle = useMemo(
    () => getSectionTitleTranslation("Advanced", language),
    [language]
  );

  const currency = formData.currency || "THB";

  const handleInlineCommit = async (fieldname: string, newValue: unknown) => {
    const previousValue = formData[fieldname];
    setEditingField(null);
    onFieldChange(fieldname, newValue);
    await saveField(fieldname, newValue, previousValue);
  };

  const handleAdvance = useCallback((fieldname: string, direction: "next" | "prev") => {
    const next = getAdjacentField(fieldSchemas, fieldname, direction);
    if (next) setEditingField(next);
  }, [fieldSchemas]);

  return (
    <>
      <div>
        {sectionTitle && <SectionHeader label={sectionTitle} textSize={textSize} />}

        <div className={CARD}>
          {/* Collapse/Expand header — always clickable, unaffected by edit mode */}
          <button
            type="button"
            className={cn("w-full flex items-center gap-3 text-left", rowPx, rowPy)}
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            <span className="text-sm font-medium flex-1">Advanced</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                isExpanded && "rotate-180"
              )}
            />
          </button>

          {isExpanded && (
            <div
              className={cn(
                editMode === "modal" && !disabled && "cursor-pointer",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={
                editMode === "modal"
                  ? () => { if (!disabled) setModalOpen(true); }
                  : undefined
              }
            >
              <div className={cn(dividerMx, "border-t border-border/40")} />
              <div className="space-y-0">
                {fieldSchemas.map((field, index) => {
                  const value = formData[field.fieldname];
                  const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
                  const isLast = index === fieldSchemas.length - 1;

                  return (
                    <InlineFieldRow
                      key={field.fieldname}
                      field={field}
                      value={value}
                      label={resolvedLabel.label}
                      editMode={editMode}
                      isEditing={editingField === field.fieldname}
                      onStartEdit={() => setEditingField(field.fieldname)}
                      onCommit={(v) => handleInlineCommit(field.fieldname, v)}
                      onCancel={() => setEditingField(null)}
                      saveState={getFieldState(field.fieldname)}
                      currency={currency}
                      onAdvance={(dir) => handleAdvance(field.fieldname, dir)}
                      onRetry={() => retryField(field.fieldname)}
                      error={fieldErrors?.[field.fieldname]}
                      onClearError={() => onClearFieldError?.(field.fieldname)}
                      rowPx={rowPx}
                      rowPy={rowPy}
                      rowGap={rowGap}
                      rowLabel={rowLabel}
                      dividerMx={dividerMx}
                      isLast={isLast}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={sectionTitle || "Advanced"}
        fields={fieldSchemas}
        section={undefined}
        formData={formData}
        onFieldChange={onFieldChange}
        onSubmit={onSubmit}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
        documentName={formData?.name}
      />
    </>
  );
}
